import { Container } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import {
	BoltIcon,
	MagnifyingGlassIcon,
	ShieldCheckIcon,
	SignalIcon,
	PresentationChartLineIcon,
	ClockIcon,
	ArrowRightIcon,
	PlayIcon
} from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import { CLUB_ROUTES } from "../constants/clubs";

const Home = () => {
	const navigate = useNavigate();
	const [query, setQuery] = useState("");

	const filteredRoutes = useMemo(
		() =>
			CLUB_ROUTES.filter(route => route.name.toLowerCase().includes(query.toLowerCase().trim())),
		[query]
	);

	const features = [
		{
			icon: PresentationChartLineIcon,
			title: "Advanced Trendlines",
			description:
				"Track batting and bowling trajectories across years, grades, and selected squads."
		},
		{
			icon: ShieldCheckIcon,
			title: "Reliable Match Data",
			description: "Built on official score sources to keep captain decisions grounded in evidence."
		},
		{
			icon: SignalIcon,
			title: "Live Command View",
			description:
				"Switch quickly between statistics, career insights, and season-by-season patterns."
		},
		{
			icon: ClockIcon,
			title: "Fast Scouting",
			description: "Use filters and visual comparisons to identify form players in seconds."
		}
	];

	// const highlights = [
	// 	{ label: "Tracked Teams", value: CLUB_ROUTES.length.toString().padStart(2, "0") },
	// 	{ label: "Insight Modes", value: "03" },
	// 	{ label: "Data Source", value: "Official" }
	// ];

	return (
		<div className="min-h-screen">
			<Header />

			<section className="relative overflow-hidden">
				<div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_5%,rgba(255,159,28,.16),transparent_30%),radial-gradient(circle_at_85%_15%,rgba(29,211,176,.18),transparent_34%)]"></div>
				<Container size="xl" className="relative py-12 md:py-20">
					<div className="grid lg:grid-cols-[1.2fr_.8fr] gap-8 items-center">
						<div className="space-y-6">
							{/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/65 border border-slate-600 text-slate-200">
								<SparklesIcon className="h-5 w-5 text-orange-300" />
								<span className="text-sm uppercase tracking-[0.16em]">
									Sports Intelligence Deck
								</span>
							</div> */}
							<h1 className="text-5xl sm:text-6xl md:text-8xl leading-[0.9]">
								<span className="bg-gradient-to-r from-orange-300 via-amber-200 to-emerald-300 bg-clip-text text-transparent">
									Inside Edge
								</span>
							</h1>
							<p className="text-slate-300 max-w-2xl text-base sm:text-lg md:text-2xl leading-relaxed">
								Cut through noise with sharp cricket analytics built for clubs and fans who care
								about form, momentum, and match-winning detail.
							</p>
							<div className="flex flex-wrap items-center gap-3">
								<button
									onClick={() => navigate(`/stats/${CLUB_ROUTES[0].key}`)}
									className="inline-flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-slate-900 text-sm sm:text-base font-bold shadow-xl"
								>
									<PlayIcon className="h-5 w-5" />
									Launch Command Center
								</button>
								<div className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-900/65 border border-slate-600 text-slate-200">
									<BoltIcon className="h-4 w-4 text-emerald-300" />
									<span className="text-sm">Beta build: work in progress</span>
								</div>
							</div>
						</div>
						{/* <div className="panel top-border-glow rounded-3xl p-5 md:p-8">
							<p className="text-slate-100 uppercase tracking-[0.14em] text-sm mb-4">
								Pulse Snapshot
							</p>
							<div className="space-y-3">
								{highlights.map(metric => (
									<div
										key={metric.label}
										className="flex items-center justify-between rounded-xl border border-slate-600 bg-slate-900/55 px-4 py-3"
									>
										<span className="text-slate-300 text-sm">{metric.label}</span>
										<span className="text-2xl md:text-3xl text-white">{metric.value}</span>
									</div>
								))}
							</div>
						</div> */}
					</div>
				</Container>
			</section>

			<section className="py-8 md:py-16">
				<Container size="xl">
					<div className="mb-8 md:mb-10">
						<h2 className="text-3xl md:text-5xl text-white mb-2">What Makes It Powerful</h2>
						<p className="text-slate-300 text-base md:text-xl max-w-2xl">
							Purpose-built tooling for tactical reviews, selection calls, and fan storytelling.
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
						{features.map(feature => (
							<div key={feature.title} className="panel p-6 rounded-2xl card-hover">
								<div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-400 rounded-xl flex items-center justify-center mb-4">
									<feature.icon className="h-6 w-6 text-white" />
								</div>
								<h3 className="text-2xl md:text-3xl text-white mb-2">{feature.title}</h3>
								<p className="text-slate-300 text-sm md:text-base">{feature.description}</p>
							</div>
						))}
					</div>
				</Container>
			</section>

			<section className="py-8 md:py-16">
				<Container size="xl">
					<div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
						<div>
							<h2 className="text-3xl md:text-5xl text-white">Team Command Center</h2>
							<p className="text-slate-300 text-base md:text-xl mt-2">
								Pick a club and open a full analytics dashboard.
							</p>
						</div>
						<div className="relative max-w-md w-full">
							<MagnifyingGlassIcon className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
							<input
								value={query}
								onChange={e => setQuery(e.target.value)}
								placeholder="Search teams"
								className="w-full rounded-xl border border-slate-600 bg-slate-900/70 pl-10 pr-4 py-3 text-slate-100 placeholder:text-slate-400 outline-none focus:border-emerald-400"
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
						{filteredRoutes.map(route => (
							<div
								key={route.key}
								onClick={() => navigate("/stats/" + route.key)}
								className="group cursor-pointer"
							>
								<div className="panel p-6 rounded-2xl card-hover">
									<div
										className={`w-full h-24 bg-gradient-to-r ${route.accent} rounded-xl mb-4 flex items-center justify-between px-4 relative overflow-hidden`}
									>
										<div className="absolute inset-0 bg-black/15"></div>
										<span className="text-white font-bold text-xl relative z-10">
											{route.name.charAt(0)}
										</span>
										<span className="text-white/90 text-xs uppercase tracking-[0.12em] relative z-10">
											Club Deck
										</span>
									</div>
									<div className="flex items-center justify-between">
										<div>
											<h3 className="text-xl md:text-2xl text-white mb-1">{route.name}</h3>
											{/* <p className="text-slate-300 text-sm">{route.tagline}</p> */}
										</div>
										<ArrowRightIcon className="h-5 w-5 text-slate-400 group-hover:text-emerald-300 group-hover:translate-x-1 transition-all duration-200" />
									</div>
								</div>
							</div>
						))}
						{filteredRoutes.length === 0 && (
							<div className="col-span-full panel rounded-2xl p-8 text-center text-slate-300">
								No teams matched that search.
							</div>
						)}
					</div>
				</Container>
			</section>

			<Footer />
		</div>
	);
};

export default Home;
